import React, { useContext, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import moment from "moment";

import AdminLayout from "../../components/Admin/AdminLayout";
import AdminPageShell from "../../components/Admin/AdminPageShell";
import { adminTableStyles } from "../../components/Admin/adminTableStyles";
import { generatePDF, generateLaPosteCSV } from "../../components/Admin/helper";
import { api } from "../../api/api";
import AuthContext from "../../contexts/AuthCtx";
import { toast } from "../../utils/toast";
import { DELIVERY_STATUS } from "../../utils/letterSend";

const deliveryOptions = [
  { value: DELIVERY_STATUS.PENDING, label: "En attente" },
  { value: DELIVERY_STATUS.IN_PROGRESS, label: "En cours" },
  { value: DELIVERY_STATUS.RECEIVED, label: "Reçue" },
];

function LifetimeExpedition() {
  const AuthCtx = useContext(AuthContext);
  const [letters, setLetters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const loadLetters = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get("admin/getLifetimeLetters", {
        headers: { Authorization: `Bearer ${AuthCtx.userToken}` },
      });
      setLetters(data.letters || []);
    } catch (err) {
      if (err.response?.status === 404) {
        setLetters([]);
        toast.info(
          "La liste d'envois immédiats sera disponible dès que le backend sera mis à jour."
        );
      } else {
        toast.error("Impossible de charger les envois immédiats.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLetters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AuthCtx.userToken]);

  const updateDeliveryStatus = async (recipientId, deliveryStatus) => {
    setUpdatingId(recipientId);
    try {
      await api.put(
        `admin/updateDeliveryStatus/${recipientId}`,
        { deliveryStatus },
        { headers: { Authorization: `Bearer ${AuthCtx.userToken}` } }
      );
      await loadLetters();
      toast.success("Statut mis à jour.");
    } catch (err) {
      if (err.response?.status === 404) {
        toast.info(
          "La mise à jour du statut sera disponible dès que le backend sera mis à jour."
        );
      } else {
        toast.error(
          err.response?.data?.message || "Erreur lors de la mise à jour."
        );
      }
    } finally {
      setUpdatingId(null);
    }
  };

  const rows = letters.flatMap((letter) =>
    (letter.recipients || []).map((recipient) => ({
      id: `${letter._id}-${recipient._id}`,
      letterId: letter._id,
      recipientId: recipient._id,
      userName: [
        letter.userId?.user_info?.firstName,
        letter.userId?.user_info?.birthName,
      ]
        .filter(Boolean)
        .join(" "),
      userEmail: letter.userId?.email || "—",
      letterTitle: letter.title,
      recipientName: [recipient.firstName, recipient.lastName]
        .filter(Boolean)
        .join(" "),
      deliveryStatus: recipient.deliveryStatus,
      // Only inProgressAt — sentAt is set on send while still "en attente"
      inProgressAt: recipient.inProgressAt || null,
      letter,
      recipient,
    }))
  );

  const columns = [
    {
      name: "Utilisateur",
      cell: (row) => (
        <div className="py-1">
          <div className="font-josefin text-sm text-secondaryText">
            {row.userName || "—"}
          </div>
          <div className="font-josefin text-xs text-textMuted">
            {row.userEmail}
          </div>
        </div>
      ),
      grow: 1.8,
      wrap: true,
    },
    {
      name: "Lettre",
      selector: (row) => row.letterTitle,
      grow: 1.2,
      wrap: true,
    },
    {
      name: "Destinataire",
      selector: (row) => row.recipientName,
      grow: 1.2,
      wrap: true,
    },
    {
      name: "En cours depuis",
      selector: (row) => {
        const showDate =
          row.deliveryStatus === DELIVERY_STATUS.IN_PROGRESS ||
          row.deliveryStatus === DELIVERY_STATUS.RECEIVED;
        return showDate && row.inProgressAt
          ? moment(row.inProgressAt).format("DD/MM/YYYY HH:mm")
          : "—";
      },
      width: "160px",
    },
    {
      name: "Statut",
      cell: (row) => (
        <select
          value={row.deliveryStatus || DELIVERY_STATUS.PENDING}
          disabled={updatingId === row.recipientId}
          onChange={(e) =>
            updateDeliveryStatus(row.recipientId, e.target.value)
          }
          className="rounded-md border border-greyLight bg-white px-2 py-1.5 font-josefin text-sm text-secondaryText outline-none focus:border-brandPurple"
        >
          {deliveryOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ),
      ignoreRowClick: true,
      width: "150px",
    },
    {
      name: "Contenu",
      cell: (row) => (
        <button
          type="button"
          onClick={() => generatePDF(row.letterTitle, row.letter.content)}
          className="font-josefin text-sm text-brandPurple underline-offset-2 hover:underline"
        >
          PDF
        </button>
      ),
      width: "80px",
    },
    {
      name: "Export",
      cell: (row) => (
        <button
          type="button"
          onClick={() => generateLaPosteCSV([row.recipient])}
          className="font-josefin text-sm text-brandPurple underline-offset-2 hover:underline"
        >
          CSV
        </button>
      ),
      width: "90px",
    },
  ];

  return (
    <AdminLayout>
      <AdminPageShell
        title="Envois immédiats"
        subtitle="Lettres envoyées de ton vivant. Mets à jour le statut de livraison pour chaque destinataire."
        isLoading={isLoading}
      >
        <DataTable
          columns={columns}
          data={rows}
          noHeader
          highlightOnHover
          pagination
          responsive
          customStyles={adminTableStyles}
          noDataComponent={
            <div className="py-12 text-center font-josefin text-sm text-textMuted">
              Aucun envoi immédiat pour le moment.
            </div>
          }
        />
      </AdminPageShell>
    </AdminLayout>
  );
}

export default LifetimeExpedition;
